import type { FlowType } from '../types'

/**
 * The booking journey as data.
 *
 * A flow is an ordered list of steps, which is why tyres and MOT are entries
 * in a table rather than a second widget.
 *
 * The journey opens with the registration. Knowing the vehicle lets the widget
 * lead with a reason to book — "your MOT expires in 18 days" — and address the
 * customer's actual car rather than showing a generic list. The plate is
 * skippable unless the channel requires a confirmed lookup, so nobody away
 * from their car is stuck on the first screen.
 */
export type StepId =
  | 'build'
  | 'vehicle'
  | 'tyres'
  | 'tyreOffers'
  | 'extras'
  | 'schedule'
  | 'details'
  | 'confirmation'

export interface StepDefinition {
  id: StepId
  /** Shown in the progress indicator. */
  label: string
  /** Terminal steps sit outside the progress count. */
  terminal?: boolean
  /**
   * Steps that share a progress position, named rather than pointed at a
   * leader. Picking a job type and then picking which service within it are one
   * idea to the customer — "what do you need" — so the progress bar should not
   * lurch forward between them.
   *
   * A name rather than a leader because the members are not in the same order in
   * every flow: a tyre-only channel opens on the size, while a general booking
   * reaches it after the cross-sell. Whichever comes first holds the position.
   */
  group?: string
}

const STEP_LIBRARY: Record<StepId, StepDefinition> = {
  vehicle: { id: 'vehicle', label: 'Your vehicle' },
  // One surface for the whole booking: pick a category, choose within it, add
  // anything else. See BuildStep for why this replaced three separate steps.
  build: { id: 'build', label: 'What you need', group: 'need' },
  // Settling the work and then pinning down the details of it is one idea to the
  // customer, so these share a position.
  tyres: { id: 'tyres', label: 'Your tyres', group: 'work' },
  // Choosing the product needs the size, so it follows.
  tyreOffers: { id: 'tyreOffers', label: 'Your tyres', group: 'work' },
  extras: { id: 'extras', label: 'Anything else', group: 'work' },
  schedule: { id: 'schedule', label: 'Date & time' },
  details: { id: 'details', label: 'Your details' },
  confirmation: { id: 'confirmation', label: 'Booked', terminal: true },
}

const FLOW_STEPS: Record<FlowType, StepId[]> = {
  /**
   * The cross-sell comes before the tyre size on purpose.
   *
   * Somebody booking brakes, an alignment and tyres was asked to go and read a
   * sidewall the moment they finished choosing, as though the whole booking were
   * about tyres. Worse, tyres added at the cross-sell step were never given a
   * size at all — the step had already been passed — and the garage received a
   * tyre booking with nothing to order against.
   *
   * Settling everything first and then asking the one detail tyres need fixes
   * both, and means the question always follows the decision that caused it.
   */
  general: ['vehicle', 'build', 'tyres', 'tyreOffers', 'schedule', 'details', 'confirmation'],
  /**
   * A tyre-only channel opens on one combined screen: registration or typed
   * size, then how many. No separate vehicle step — a customer who knows their
   * size should not have to resolve a plate first, and the registration the
   * booking needs is collected with their other details once they have seen the
   * prices.
   */
  tyres: ['tyres', 'tyreOffers', 'extras', 'schedule', 'details', 'confirmation'],
  /**
   * An MOT channel is the general journey with the answer filled in: the test is
   * already in the basket when the build surface opens, so the customer sees what
   * they came for as done and everything else as optional.
   */
  mot: ['vehicle', 'build', 'tyres', 'tyreOffers', 'schedule', 'details', 'confirmation'],
}

export interface FlowOptions {
  flowType: FlowType
  /** Only ask about tyre sizes when a tyre job was actually chosen. */
  includeTyres?: boolean
  /** Only show the product list when a supplier can actually price tyres. */
  includeTyreOffers?: boolean
  /** Drop the cross-sell step when there is nothing left to offer. */
  includeExtras?: boolean
}

/** Builds the ordered steps for a flow. */
export function buildFlow({
  flowType,
  includeTyres,
  includeTyreOffers,
  includeExtras,
}: FlowOptions): StepDefinition[] {
  const ids = FLOW_STEPS[flowType] ?? FLOW_STEPS.general

  return ids
    .filter((id) => {
      if (id === 'tyres') return includeTyres !== false
      // No supplier configured means no products to choose from; the booking
      // still captures the size and the garage quotes on arrival.
      if (id === 'tyreOffers') return includeTyres !== false && includeTyreOffers !== false
      if (id === 'extras') return includeExtras !== false
      return true
    })
    .map((id) => STEP_LIBRARY[id])
}

/**
 * Distinct progress positions. Steps sharing a group collapse into whichever of
 * them comes first in this flow, and terminal steps are excluded.
 *
 * Taking the first present member rather than a named leader is what lets the
 * same group survive being reordered: the cross-sell holds the position in a
 * general booking, the size step holds it in a tyre-only one, and a flow with
 * only one of them still counts it once.
 */
export function progressSteps(steps: StepDefinition[]): StepDefinition[] {
  const seen = new Set<string>()

  return steps.filter((step) => {
    if (step.terminal) return false
    if (!step.group) return true
    if (seen.has(step.group)) return false

    seen.add(step.group)
    return true
  })
}

export function stepIndex(steps: StepDefinition[], id: StepId): number {
  return steps.findIndex((step) => step.id === id)
}

/** The step before `id`, or null when already at the start. */
export function previousStep(steps: StepDefinition[], id: StepId): StepDefinition | null {
  const index = stepIndex(steps, id)
  return index > 0 ? steps[index - 1] : null
}

/** The step after `id`, or null when there is nowhere further to go. */
export function nextStep(steps: StepDefinition[], id: StepId): StepDefinition | null {
  const index = stepIndex(steps, id)
  if (index < 0 || index >= steps.length - 1) return null
  return steps[index + 1]
}

export interface ProgressPosition {
  current: number
  total: number
  label: string
}

/**
 * Position for the progress bar. Terminal steps report as complete rather
 * than as an extra step, so the bar fills on confirmation.
 */
export function progressFor(steps: StepDefinition[], id: StepId): ProgressPosition | null {
  const counted = progressSteps(steps)
  if (counted.length === 0) return null

  const definition = steps.find((step) => step.id === id)
  if (!definition) return null

  if (definition.terminal) {
    return { current: counted.length, total: counted.length, label: definition.label }
  }

  // A grouped step reports the position of the first of its group, so the bar
  // holds still while the customer works through them. The label stays this
  // step's own, so the wording still describes the screen they are looking at.
  const index = counted.findIndex((step) =>
    definition.group ? step.group === definition.group : step.id === definition.id
  )
  if (index < 0) return null

  return { current: index + 1, total: counted.length, label: definition.label }
}
